import { useState, useEffect, useCallback } from "react";
import { listUsers, createUser, toggleUserActive, updateUserRole } from "../../services/api";
import { ArrowRightIcon } from "../Icons";
import "./Admin.css";

const EMPTY_FORM = { name: "", email: "", password: "", is_admin: false };

export default function AdminPanel() {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [formError,   setFormError]   = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await listUsers();
      setUsers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormLoading(true);
    try {
      await createUser(form);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Erro ao criar usuário.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    await toggleUserActive(id);
    await load();
  };

  const handleToggleRole = async (id, currentIsAdmin) => {
    await updateUserRole(id, !currentIsAdmin);
    await load();
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <p className="admin-eyebrow">Administração</p>
        <h1 className="admin-title">Usuários</h1>
        <p className="admin-desc">Gerencie os acessos ao sistema.</p>
      </div>

      <div className="admin-body">
        {/* Tabela de usuários */}
        <section className="admin-section">
          <h2 className="admin-section-title">Usuários cadastrados</h2>
          {loading ? (
            <p className="admin-loading">Carregando...</p>
          ) : (
            <div className="users-table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? "row-inactive" : ""}>
                      <td>{u.name}</td>
                      <td className="td-email">{u.email}</td>
                      <td>
                        <span className={`badge ${u.is_admin ? "badge-admin" : "badge-tech"}`}>
                          {u.is_admin ? "admin" : "técnico"}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${u.is_active ? "badge-active" : "badge-inactive"}`}>
                          {u.is_active ? "ativo" : "inativo"}
                        </span>
                      </td>
                      <td className="td-actions">
                        <button
                          className="btn-action"
                          onClick={() => handleToggleRole(u.id, u.is_admin)}
                        >
                          <ArrowRightIcon size={12} /> {u.is_admin ? "técnico" : "admin"}
                        </button>
                        <button
                          className={`btn-action ${u.is_active ? "btn-action-danger" : "btn-action-success"}`}
                          onClick={() => handleToggleActive(u.id)}
                        >
                          {u.is_active ? "Desativar" : "Ativar"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Formulário novo usuário */}
        <section className="admin-section">
          <h2 className="admin-section-title">Novo usuário</h2>
          <form onSubmit={handleCreate} className="admin-form">
            <div className="admin-form-row">
              <div className="field">
                <label>Nome</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Nome completo"
                  required
                />
              </div>
              <div className="field">
                <label>E-mail</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="nome@empresa.com"
                  required
                />
              </div>
            </div>
            <div className="admin-form-row">
              <div className="field">
                <label>Senha</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Senha inicial"
                  required
                  minLength={6}
                />
              </div>
              <div className="field admin-role-field">
                <label>Role</label>
                <select
                  value={form.is_admin ? "admin" : "tecnico"}
                  onChange={(e) => setForm(f => ({ ...f, is_admin: e.target.value === "admin" }))}
                  className="admin-select"
                >
                  <option value="tecnico">Técnico</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {formError && <div className="admin-error">{formError}</div>}

            <button type="submit" className="btn-primary" disabled={formLoading}>
              {formLoading ? <><span className="spinner" /> Criando...</> : "Criar usuário"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
